<template>
<m-pageframe
    title="RedChirp Weekly Sales Forecast"
>
<v-container
    fluid
>
    <v-text-field
        autocomplete="off"
        label="Search"
        prepend-icon="mdi-magnify"
        v-model="full_text"
        v-on:input="update"
    >
        <v-menu
            bottom
            offset-y
            slot="append-outer"
        >
            <template v-slot:activator="{ on }">
                <v-icon
                    v-on="on"
                >
                    mdi-dots-vertical
                </v-icon>
            </template>
    
            <v-list>
                <v-list-item
                    v-on:click="onClickExport()"
                >
                    <v-list-item-title>Export to Excel...</v-list-item-title>
                </v-list-item>
            </v-list>
        </v-menu> 
    </v-text-field>

   <v-select
        label="Scenario"
        v-model="scenario"
        :items="scenarios"
   />

    <v-data-table
        v-if="results != null"
        :headers="headers"
        :items="results"
        :items-per-page="100"
        class="elevation-1"
        :hide-default-footer="true"
        disable-sort
        :footer-props="{
            disableItemsPerPage: false,
            disablePagination: false,
        }"
    >
    
        <template v-slot:item.quality_factor="{ item }">
            {{$rc.pretty(item.quality_factor_milli_pct,'milli_pct')}}
        </template>

        <template v-slot:item.target_unadjusted="{ item }">
            {{$rc.pretty(getTargetUnadjusted(item), 'millicents_whole_dollars_accounting')}}
        </template>

         <template v-slot:item.target_adjusted="{ item }">
            {{$rc.pretty(getTargetAdjusted(item), 'millicents_whole_dollars_accounting')}}
        </template>

        <template v-slot:item.actual="{ item }">
            {{$rc.pretty(item.actual_sales_milli_cents, 'millicents_whole_dollars_accounting')}}
        </template>

        <template v-slot:item.sales_delta="{ item }">
            {{$rc.pretty(getRateDela(item), 'millicents_whole_dollars_accounting')}}
        </template>

        <template v-slot:item.target_mav="{ item }">
            {{$rc.pretty(getTargetMav(item), 'millicents_whole_dollars_accounting')}}
        </template>

        <template v-slot:item.actual_mav="{ item }">
            {{$rc.pretty(item.actual_mav_milli_cents, 'millicents_whole_dollars_accounting')}}
        </template>

        <template v-slot:item.delta_mav="{ item }">
            {{$rc.pretty(getDeltaMav(item), 'millicents_whole_dollars_accounting')}}
        </template>

        


         <template v-slot:item.action="{ item }">
            <v-menu 
                bottom 
                offset-y  
            >
            
            <template v-slot:activator="{ on }">
                    
                <v-icon 
                    v-on="on"
                >
                    mdi-dots-vertical-circle-outline
                </v-icon>
                    
            </template>
            
            <v-list>
                <v-list-item
                    v-on:click="onClickSetActual(item)"
                    :disabled="!$rc.$state.hasRedChirpStaffRoleAdministrator()"
                >
                    <v-list-item-title>Set Actual...</v-list-item-title>
                </v-list-item>
            
            </v-list>
            
            </v-menu> 
        </template>
    
    </v-data-table>

    <m-customize-rate-card-value-dialog
        ref="customize_dialog"
    />

</v-container>
</m-pageframe>
</template>

<script>
const _ = require('lodash');

export default {
    data() {
        return {
            scenario: 'green',
            scenarios: [
                { 'text': 'Red', 'value': 'red' },
                { 'text': 'Yellow', 'value': 'yellow' },
                { 'text': 'Green', 'value': 'green' },
                { 'text': 'Super Green', 'value': 'super_green' },
            ],

            full_text: null,
            results: [],

            headers: [
                { text: 'Quarter', value: 'quarter' },
                { text: 'Week', value: 'week' },
                { text: 'Start', value: 'start_date' },
                { text: 'Stop', value: 'end_date' },
                { text: 'Notes', value: 'notes' },
                { text: 'Seasonality', value: 'quality_factor' },
                //{ text: 'Sales Rate (Unadjusted)', value: 'target_unadjusted' },
                { text: 'Sales: Target', value: 'target_adjusted' },
                { text: 'Sales: Actual', value: 'actual' },
                { text: 'Sales: Delta', value: 'sales_delta' },

                { text: 'MAV: Target', value: 'target_mav' },
                { text: 'MAV: Actual', value: 'actual_mav' },
                { text: 'MAV: Delta', value: 'delta_mav' },


                { text: 'Action', value: 'action' },
            ],
        }
    },

    mounted () {
        this.doUpdate();
    },

    methods: {
        update: _.debounce(function(){
            this.doUpdate();
        }, 751),
        
        doUpdate()
        {
             this.$rc.rest({
                'spinner': false,
                'endpoint': 'LIST: /api/v1/admin/red-chirp-sales-performance',
            
                'params' : {
                        'full_text': this.full_text,
                    },
            
                'on_success': (data) => {
                        this.results = data.results;
                    },
            
                'on_error': 'Unable to list weekly sales forecast',
            });
        },

        getTargetUnadjusted(item)
        {
            return item[this.scenario+"_unadjusted_sales_rate"];
        },

        getTargetAdjusted(item)
        {
            return item[this.scenario+"_sales_rate"];
        },

        getTargetMav(item)
        {
            return item[this.scenario+"_ending_mav"];
        },

        getRateDela(item)
        {
            if ( item.actual_sales_milli_cents == null )
                return null;

            return item.actual_sales_milli_cents - this.getTargetAdjusted(item);
        },

        getDeltaMav(item)
        {
            if ( item.actual_mav_milli_cents == null )
                return null;

            return item.actual_mav_milli_cents - this.getTargetMav(item);
        },

        onClickSetActual(item)
        {
            let params = {
                'instructions': 'Enter the acutal sales for the week: '+item.week,
                'line_type' : 'actual sales',
                'value_type' : item.week,
                'default_value' : null,
                'effective_value' : item.actual_sales_milli_cents,
                'rate_card_label' : 'Actual Sales',
                'on_edit' : (val) => { this.doSetActual(item, val); },
                'on_default' : (val) => {}
            };

            this.$refs['customize_dialog'].showDialog(params);
        },

        doSetActual(item, actual_sales_milli_cents)
        {
            this.$rc.rest({
                'spinner': false,
                'endpoint': 'PUT: /api/v1/admin/red-chirp-sales-performance',
            
                'params' : {
                        'week': item.week,
                        'actual_sales_milli_cents': actual_sales_milli_cents,
                    },
            
                'on_success': (data) => {
                        this.doUpdate();
                    },
            
                'on_error': 'Unable to save actual sales',
            });
        },

        onClickExport() {
            this.$rc.excelExport(
                    'EXPORT: /api/v1/admin/red-chirp-sales-performance',
                    {
                    }
                );
        },
    }
}
</script>

<style lang="scss" scoped>
</style>